import type { CommunityCupDivisionData, CommunityCupFixture, FinalistOverride } from "@/types/tournament";

// Re-exported for existing/future importers of this module — the type
// itself now lives in types/tournament.ts (see FinalistOverride there for
// why) since CommunityCupDivisionData.finalistOverride needs it and
// types/tournament.ts can't import from utils/.
export type { FinalistOverride };

export interface CommunityScore {
  side: "A" | "B";
  matchWins: number;
  gamesWon: number;
  gamesAgainst: number;
}

export interface CommunityCupJokerRow {
  round_no: number;
  community_side: "A" | "B";
  pair_seed: number | null;
  revealed_at: string | null;
}

export interface CommunityCupScoreRow {
  round_no: number;
  court_no: number;
  set_no: number;
  seed_a: number;
  seed_b: number;
  score_a: number | null;
  score_b: number | null;
  winner_side: string | null;
  entered_at?: string | null;
}

/**
 * The single source of truth for Community Cup's round structure: rounds 1-5
 * are the round-robin, round 6 is the division final. Every page/util that
 * previously redefined its own `ROUNDS`/`FINAL_ROUND` constants (or hardcoded
 * the bare literals) should import these instead, so the round structure
 * only has to change in one place.
 */
export const ROUND_ROBIN_ROUNDS = [1, 2, 3, 4, 5] as const;
export const FINAL_ROUND = 6 as const;

/**
 * The default round-robin round count for a division that predates the
 * roundsCount field. Every existing persisted Community Cup division —
 * pairsPerSide 5 or 8 alike — plays exactly 5 round-robin rounds today, so
 * this is the correct default for ALL of them, not pairsA.length (which
 * would wrongly give an 8-pairsPerSide legacy division 8 rounds).
 */
const DEFAULT_ROUNDS_COUNT = 5;

/**
 * Computes a division's round-robin round numbers (1..roundsCount) from its
 * (optional) roundsCount field, defaulting to 5 to match every existing
 * division. Every reader of "what rounds does this division play" should
 * go through this instead of the fixed ROUND_ROBIN_ROUNDS constant.
 */
export function getRoundRobinRounds(
  config: Pick<CommunityCupDivisionData, "roundsCount">,
): number[] {
  const count = config.roundsCount ?? DEFAULT_ROUNDS_COUNT;
  return Array.from({ length: count }, (_, i) => i + 1);
}

/** A division's final round number: roundsCount + 1 (6 for the default 5-round division). */
export function getFinalRound(
  config: Pick<CommunityCupDivisionData, "roundsCount">,
): number {
  return (config.roundsCount ?? DEFAULT_ROUNDS_COUNT) + 1;
}

/**
 * Builds a fresh `jokerWindows` object with every round-robin round set to
 * the same status. This is the single source of truth for the "default"
 * jokerWindows shape — a new division starts with every round `"not_open"`;
 * an organizer-triggered reset re-opens every round as `"pending"`.
 * `roundsCount` defaults to 5 so every existing call site (not yet updated
 * to pass one explicitly) behaves identically to before.
 */
export function createJokerWindows(
  status: "not_open" | "pending" = "not_open",
  roundsCount: number = DEFAULT_ROUNDS_COUNT,
): CommunityCupDivisionData["jokerWindows"] {
  const windows: CommunityCupDivisionData["jokerWindows"] = {};
  for (let round = 1; round <= roundsCount; round++) {
    windows[round] = { status };
  }
  return windows;
}

export function getJokerWindowRoundNo(fixtureRound: number): number {
  return fixtureRound;
}

/**
 * Converts legacy `window1`/`window2`/`window3` keys to per-round numeric
 * keys (1-5), and promotes any `"not_open"` round to `"pending"` (all
 * round-robin rounds are open for picks from the moment a division's roster
 * is ready — `"not_open"` was an older default that's no longer produced,
 * but persisted events can still have it). Already-current data (numeric
 * keys, no `"not_open"`) is returned unchanged. Extracted here (rather than
 * living only on `CommunityCupJokersPage.tsx`) so every reader of
 * `jokerWindows` — the organizer's Jokers overview grid included — sees the
 * same normalized shape instead of only whichever division a page happens
 * to have "active" at the time.
 */
export function normalizeJokerWindows(
  jokerWindows: unknown,
  roundsCount: number = DEFAULT_ROUNDS_COUNT,
): CommunityCupDivisionData["jokerWindows"] {
  const jw = jokerWindows as Record<string | number, { status?: string; lockedAt?: string } | undefined> | null | undefined;
  const toStatus = (s: string | undefined): "pending" | "locked" => (s === "locked" ? "locked" : "pending");
  const rounds = getRoundRobinRounds({ roundsCount });

  if (jw?.[1] !== undefined) {
    const windows = jw as Record<number, { status?: string; lockedAt?: string }>;
    const needsOpen = rounds.some((r) => windows[r]?.status === "not_open");
    if (!needsOpen) return windows as CommunityCupDivisionData["jokerWindows"];
    const result: CommunityCupDivisionData["jokerWindows"] = {};
    for (const r of rounds) result[r] = { status: toStatus(windows[r]?.status) };
    return result;
  }

  // Legacy window1/window2/window3 shape only ever existed for the original
  // 5-round format — this branch is intentionally not parameterized beyond
  // roundsCount validation, since no pre-multi-round-count event used it.
  const result: CommunityCupDivisionData["jokerWindows"] = {};
  const legacyMap: Record<number, string | undefined> = {
    1: jw?.["window1"]?.status,
    3: jw?.["window2"]?.status,
    5: jw?.["window3"]?.status,
  };
  for (const r of rounds) {
    result[r] = { status: legacyMap[r] !== undefined ? toStatus(legacyMap[r]) : "pending" };
  }
  return result;
}

/** Returns the joker multiplier (1, 2, or 4) for a given fixture. */
export function computeMatchMultiplier(
  fixture: CommunityCupFixture,
  jokerA: CommunityCupJokerRow | undefined,
  jokerB: CommunityCupJokerRow | undefined
): number {
  const windowRoundNo = getJokerWindowRoundNo(fixture.round);

  const aPlayed =
    jokerA?.revealed_at != null &&
    jokerA.pair_seed === fixture.seedA &&
    jokerA.round_no === windowRoundNo;
  const bPlayed =
    jokerB?.revealed_at != null &&
    jokerB.pair_seed === fixture.seedB &&
    jokerB.round_no === windowRoundNo;

  if (aPlayed && bPlayed) return 4;
  if (aPlayed || bPlayed) return 2;
  return 1;
}

/**
 * Aggregates match wins and games for both communities, respecting joker
 * multipliers. Computed per-division — `config` is always one division's
 * CommunityCupDivisionData.
 */
export function computeStandings(
  scores: CommunityCupScoreRow[],
  jokers: CommunityCupJokerRow[],
  config: CommunityCupDivisionData
): { A: CommunityScore; B: CommunityScore } {
  const result = {
    A: { side: "A" as const, matchWins: 0, gamesWon: 0, gamesAgainst: 0 },
    B: { side: "B" as const, matchWins: 0, gamesWon: 0, gamesAgainst: 0 },
  };

  for (const score of scores) {
    // Round-robin standings only ever reflect rounds 1-5 — round 6 is the
    // division final and must never feed back into the table it was seeded
    // from (see ROUND_ROBIN_ROUNDS/FINAL_ROUND above).
    if (!getRoundRobinRounds(config).includes(score.round_no)) continue;
    if (score.winner_side !== "A" && score.winner_side !== "B") continue;
    if (score.score_a == null || score.score_b == null) continue;

    const fixture = config.fixtures.find(
      (f) =>
        f.round === score.round_no &&
        f.court === score.court_no &&
        f.set === score.set_no
    );
    if (!fixture) continue;

    const windowRoundNo = getJokerWindowRoundNo(score.round_no);
    const roundJokerA = jokers.find(
      (j) => j.round_no === windowRoundNo && j.community_side === "A"
    );
    const roundJokerB = jokers.find(
      (j) => j.round_no === windowRoundNo && j.community_side === "B"
    );
    const mult = computeMatchMultiplier(fixture, roundJokerA, roundJokerB);

    if (score.winner_side === "A") {
      result.A.matchWins += mult;
    } else {
      result.B.matchWins += mult;
    }

    result.A.gamesWon += (score.score_a ?? 0) * mult;
    result.A.gamesAgainst += (score.score_b ?? 0) * mult;
    result.B.gamesWon += (score.score_b ?? 0) * mult;
    result.B.gamesAgainst += (score.score_a ?? 0) * mult;
  }

  return result;
}

// ── Pair-level standings ──────────────────────────────────────────────────────

export interface PairStanding {
  seed: number;
  side: "A" | "B";
  pts: number;        // match wins × joker multiplier
  wins: number;       // raw match wins (no multiplier)
  losses: number;     // raw match losses (decisive fixtures only — draws/unplayed don't count)
  jokerBonus: number; // pts - wins
  gameDiff: number;   // raw games won - games against
}

/** Per-pair standings for TV display. */
export function computePairStandings(
  scores: CommunityCupScoreRow[],
  jokers: CommunityCupJokerRow[],
  config: CommunityCupDivisionData,
): { A: PairStanding[]; B: PairStanding[] } {
  const byA: Record<number, PairStanding> = {};
  const byB: Record<number, PairStanding> = {};

  for (const p of config.pairsA) {
    byA[p.seed] = { seed: p.seed, side: "A", pts: 0, wins: 0, losses: 0, jokerBonus: 0, gameDiff: 0 };
  }
  for (const p of config.pairsB) {
    byB[p.seed] = { seed: p.seed, side: "B", pts: 0, wins: 0, losses: 0, jokerBonus: 0, gameDiff: 0 };
  }

  for (const score of scores) {
    // Same round-robin-only scoping as computeStandings — the final (round 6)
    // must not contaminate the round-robin pair standings it's derived from.
    if (!getRoundRobinRounds(config).includes(score.round_no)) continue;
    if (score.score_a == null || score.score_b == null) continue;
    if (score.winner_side !== "A" && score.winner_side !== "B") continue;
    const fixture = config.fixtures.find(
      (f) => f.round === score.round_no && f.court === score.court_no && f.set === score.set_no
    );
    if (!fixture) continue;
    const roundJokerA = jokers.find((j) => j.round_no === fixture.round && j.community_side === "A");
    const roundJokerB = jokers.find((j) => j.round_no === fixture.round && j.community_side === "B");
    const mult = computeMatchMultiplier(fixture, roundJokerA, roundJokerB);

    const pA = byA[fixture.seedA];
    const pB = byB[fixture.seedB];
    if (pA) {
      pA.gameDiff += score.score_a - score.score_b;
      if (score.winner_side === "A") {
        pA.pts += mult;
        pA.wins += 1;
        pA.jokerBonus += mult - 1;
      } else {
        pA.losses += 1;
      }
    }
    if (pB) {
      pB.gameDiff += score.score_b - score.score_a;
      if (score.winner_side === "B") {
        pB.pts += mult;
        pB.wins += 1;
        pB.jokerBonus += mult - 1;
      } else {
        pB.losses += 1;
      }
    }
  }

  const sortFn = (a: PairStanding, b: PairStanding) =>
    b.pts - a.pts || b.wins - a.wins || b.gameDiff - a.gameDiff || a.seed - b.seed;

  return {
    A: Object.values(byA).sort(sortFn),
    B: Object.values(byB).sort(sortFn),
  };
}

// ── Finalist determination ────────────────────────────────────────────────────

/**
 * Determines which pair on one side of the division advances to the final,
 * given that side's `PairStanding[]` (from `computePairStandings`) and the
 * seed of the *other* side's already-determined finalist.
 *
 * Tiebreak ladder: `pts -> wins -> gameDiff -> head-to-head (vs
 * opposingFinalistSeed) -> lowest seed`. See the inline `// Step N` comments
 * below for what each rung compares. `wins` is inserted ahead of `gameDiff`
 * so that a joker-multiplied win's inflated `pts` can't flip a tie that
 * `computePairStandings`' own sort (`pts -> wins -> gameDiff -> seed`) would
 * otherwise resolve on raw win count — keeping this ladder a strict
 * refinement of that sort, with head-to-head spliced in before the seed
 * fallback.
 *
 * Fixture/score lookup reuses the same pattern as `computeStandings` /
 * `computePairStandings`: fixtures are matched by seed pair to find the
 * round/court/set identity, then the score row is found by that same
 * round/court/set.
 */
export function determineFinalist(
  sideStandings: PairStanding[],
  scores: CommunityCupScoreRow[],
  jokers: CommunityCupJokerRow[],
  config: CommunityCupDivisionData,
  opposingFinalistSeed: number
): PairStanding {
  if (sideStandings.length === 0) {
    throw new Error("determineFinalist: sideStandings must not be empty");
  }

  // `jokers` isn't needed directly here: `pts` (passed in via sideStandings)
  // already has joker multipliers baked in by computePairStandings, and the
  // head-to-head comparison below cares about the recorded match winner, not
  // the multiplier that round earned. Accepted as a parameter anyway so the
  // caller can pass through the same data it already has for computePairStandings,
  // and in case a future revision needs it for a richer head-to-head rule.
  void jokers;

  // Step 1: highest pts.
  const maxPts = Math.max(...sideStandings.map((p) => p.pts));
  let candidates = sideStandings.filter((p) => p.pts === maxPts);
  if (candidates.length === 1) return candidates[0];

  // Step 2: among pts-tied pairs, highest raw wins (see JSDoc above).
  const maxWins = Math.max(...candidates.map((p) => p.wins));
  candidates = candidates.filter((p) => p.wins === maxWins);
  if (candidates.length === 1) return candidates[0];

  // Step 3: among still-tied pairs, highest overall gameDiff.
  const maxGameDiff = Math.max(...candidates.map((p) => p.gameDiff));
  candidates = candidates.filter((p) => p.gameDiff === maxGameDiff);
  if (candidates.length === 1) return candidates[0];

  // Step 4: head-to-head against the opposing side's finalist.
  const side = candidates[0].side;
  const h2h = candidates.map((pair) => {
    // Scoped to the round-robin (1-5): once a round-6 final exists between
    // these same two seeds, it would otherwise match here too — reading the
    // final's own result to decide who should have been in the final in the
    // first place. Only an actual round-robin meeting counts as head-to-head.
    const fixture = config.fixtures.find(
      (f) =>
        getRoundRobinRounds(config).includes(f.round) &&
        (side === "A"
          ? f.seedA === pair.seed && f.seedB === opposingFinalistSeed
          : f.seedB === pair.seed && f.seedA === opposingFinalistSeed)
    );

    let matchDiff = 0; // stays 0 when unplayed/not found -> falls through to step 5 unless it decides the tie
    let won = false;

    if (fixture) {
      const score = scores.find(
        (s) =>
          s.round_no === fixture.round &&
          s.court_no === fixture.court &&
          s.set_no === fixture.set
      );
      if (score && score.score_a != null && score.score_b != null) {
        const mine = side === "A" ? score.score_a : score.score_b;
        const theirs = side === "A" ? score.score_b : score.score_a;
        matchDiff = mine - theirs;
        won = score.winner_side === side;
      }
    }

    return { pair, matchDiff, won };
  });

  const winners = h2h.filter((h) => h.won);
  if (winners.length === 1) return winners[0].pair;
  // (More than one "won" against the same fixed opponent shouldn't happen in
  // practice — each seed pairing occurs at most once — so fall through to the
  // differential/seed tiebreakers below rather than special-casing it.)

  const maxMatchDiff = Math.max(...h2h.map((h) => h.matchDiff));
  const stillTied = h2h.filter((h) => h.matchDiff === maxMatchDiff).map((h) => h.pair);
  if (stillTied.length === 1) return stillTied[0];

  // Step 5: all four criteria tied — deterministic fallback (arbitrary but
  // stable) so this always returns a single pair: lowest seed wins.
  return [...stillTied].sort((a, b) => a.seed - b.seed)[0];
}

// ── Division final (round 6) ──────────────────────────────────────────────────

/**
 * True once every round-robin (rounds 1-5) fixture has a DECISIVE recorded
 * score (`score_a`/`score_b` present AND `winner_side` is `"A"`/`"B"`, never
 * a draw/null) — checked per-fixture via `.find()` rather than a length
 * comparison, since `computeStandings`/`computePairStandings` silently skip
 * drawn rows and a count-based check could pass while a draw quietly
 * contributes nothing to the standings the final is derived from.
 */
export function isRoundRobinComplete(
  scores: CommunityCupScoreRow[],
  config: CommunityCupDivisionData,
): boolean {
  const roundRobinFixtures = config.fixtures.filter((f) =>
    getRoundRobinRounds(config).includes(f.round),
  );
  if (roundRobinFixtures.length === 0) return false;

  return roundRobinFixtures.every((fixture) => {
    const score = scores.find(
      (s) => s.round_no === fixture.round && s.court_no === fixture.court && s.set_no === fixture.set,
    );
    return (
      score != null &&
      score.score_a != null &&
      score.score_b != null &&
      (score.winner_side === "A" || score.winner_side === "B")
    );
  });
}

/**
 * First round-robin round (1-5) that isn't yet fully, decisively scored —
 * "the round to show schedule/live-status for" on the TV overview display.
 * Returns `null` once every round-robin round is complete (mirrors
 * `isRoundRobinComplete`'s per-fixture decisive-score check, but stops at
 * the first incomplete round instead of requiring all of them).
 *
 * Deliberately distinct from the three other "current round" computations
 * already in this codebase (TV1's `Math.max(...scores.map(s => s.round_no))`,
 * and two organizer pages' local `useState`) — none of those mean "first
 * incomplete round," which is what a schedule look-ahead needs.
 */
export function getCurrentRound(
  scores: CommunityCupScoreRow[],
  config: CommunityCupDivisionData,
): number | null {
  for (const round of getRoundRobinRounds(config)) {
    const roundFixtures = config.fixtures.filter((f) => f.round === round);
    if (roundFixtures.length === 0) continue;
    const complete = roundFixtures.every((fixture) => {
      const score = scores.find(
        (s) => s.round_no === fixture.round && s.court_no === fixture.court && s.set_no === fixture.set,
      );
      return (
        score != null &&
        score.score_a != null &&
        score.score_b != null &&
        (score.winner_side === "A" || score.winner_side === "B")
      );
    });
    if (!complete) return round;
  }
  return null;
}

export interface DivisionFinalists {
  A: PairStanding;
  B: PairStanding;
}

/** Hard cap on fixed-point iterations in `computeDivisionFinalists` — see its JSDoc. */
const MAX_FINALIST_CONVERGENCE_ITERATIONS = 10;

function lookupOverrideStanding(
  standings: PairStanding[],
  side: "A" | "B",
  seed: number,
): PairStanding {
  const found = standings.find((p) => p.seed === seed);
  if (!found) {
    throw new Error(
      `finalistOverride.${side} references pair seed #${seed}, which is not one of side ${side}'s ` +
        `current pairs (valid seeds: ${standings.map((p) => p.seed).join(", ") || "none"}).`
    );
  }
  return found;
}

/**
 * Computes both sides' finalists for a division's round-6 final, from raw
 * round-robin scores/jokers — the pure logic behind CommunityCupLiveControl.
 * tsx's "Generate Final" action, extracted here so it's unit-testable
 * independent of that component.
 *
 * ## Manual override
 *
 * `override.A`/`override.B` (see `CommunityCupDivisionData.finalistOverride`)
 * let the organizer force either side's finalist to a specific seed,
 * bypassing `determineFinalist` for that side. If only one side is
 * overridden, the other is still computed normally via `determineFinalist`,
 * using the override seed as its `opposingFinalistSeed` (no circularity,
 * since one side is now fixed). If both are overridden, both are returned
 * directly with no ladder computation (and so no possibility of the
 * convergence error below). An override seed absent from that side's
 * current standings throws immediately, naming the bad seed and the valid
 * ones.
 *
 * ## Two-sided circularity (no override)
 *
 * `determineFinalist()`'s head-to-head tiebreak rung needs the OPPOSING
 * side's finalist seed already known, which is circular when either side's
 * own tiebreak could depend on the other's answer. Resolved by iterating to
 * a fixed point:
 *   1. Seed a starting guess for finalist A using side B's own pts-sorted
 *      leader (`computePairStandings` already returns each side sorted
 *      pts -> wins -> gameDiff -> seed, so `[0]` is a reasonable starting
 *      opponent whenever side B has no pts tie — the overwhelming majority
 *      of cases; when it IS tied, the loop below corrects for it anyway).
 *   2. Repeatedly recompute `finalistA` from the current `finalistB`, then
 *      `finalistB` from that new `finalistA`. If a full round of that
 *      recomputation leaves BOTH seeds unchanged, the pair is a fixed point:
 *      `finalistA` was computed against the very `finalistB` being returned,
 *      and vice versa — i.e. self-consistent — so we return it.
 *
 * ## Convergence argument and the iteration cap
 *
 * Each side's `determineFinalist` result is drawn from a finite set (that
 * side's tied candidates, bounded by the number of pairs in the division),
 * so the sequence of `(finalistA, finalistB)` pairs produced by repeatedly
 * applying step 2 must eventually revisit a pair it has seen before
 * (pigeonhole). From that point on it is a strict cycle. A cycle of length 1
 * is exactly the fixed point in step 2 above and returns normally. A cycle
 * of length > 1 (a genuine oscillation — both sides' tiebreaks depend on
 * each other in a way with no stable resolution, e.g. a doubly-simultaneous
 * tie reaching head-to-head on both sides at once) will never satisfy the
 * "both unchanged" check no matter how long we iterate. `MAX_FINALIST_CONVERGENCE_ITERATIONS`
 * (10) is comfortably larger than any realistic division's candidate count
 * (so any genuine fixed point is reached well before the cap), but finite —
 * once hit, we don't guess: we throw a clear, actionable error naming the
 * oscillating pairs, and point the organizer at the manual override as the
 * way out (rather than silently returning one arbitrary snapshot from the
 * cycle, which is exactly the bug this replaces — the old single-correction
 * version returned whatever the SECOND pass produced without ever
 * rechecking it against a third).
 *
 * Throws (via `determineFinalist`) if either side has zero pairs.
 */
export function computeDivisionFinalists(
  scores: CommunityCupScoreRow[],
  jokers: CommunityCupJokerRow[],
  config: CommunityCupDivisionData,
  override?: FinalistOverride,
): DivisionFinalists {
  const pairStandings = computePairStandings(scores, jokers, config);
  if (pairStandings.A.length === 0 || pairStandings.B.length === 0) {
    throw new Error("Both sides need at least one pair before a final can be generated.");
  }

  const overrideA =
    override?.A != null ? lookupOverrideStanding(pairStandings.A, "A", override.A) : undefined;
  const overrideB =
    override?.B != null ? lookupOverrideStanding(pairStandings.B, "B", override.B) : undefined;

  if (overrideA && overrideB) {
    return { A: overrideA, B: overrideB };
  }
  if (overrideA) {
    return { A: overrideA, B: determineFinalist(pairStandings.B, scores, jokers, config, overrideA.seed) };
  }
  if (overrideB) {
    return { A: determineFinalist(pairStandings.A, scores, jokers, config, overrideB.seed), B: overrideB };
  }

  let finalistA = determineFinalist(pairStandings.A, scores, jokers, config, pairStandings.B[0].seed);
  let finalistB = determineFinalist(pairStandings.B, scores, jokers, config, finalistA.seed);

  for (let i = 0; i < MAX_FINALIST_CONVERGENCE_ITERATIONS; i++) {
    const nextA = determineFinalist(pairStandings.A, scores, jokers, config, finalistB.seed);
    const nextB = determineFinalist(pairStandings.B, scores, jokers, config, nextA.seed);
    if (nextA.seed === finalistA.seed && nextB.seed === finalistB.seed) {
      return { A: nextA, B: nextB };
    }
    finalistA = nextA;
    finalistB = nextB;
  }

  throw new Error(
    `Could not settle on a self-consistent pair of finalists for this division after ` +
      `${MAX_FINALIST_CONVERGENCE_ITERATIONS} attempts - the tiebreak keeps oscillating (most recently ` +
      `between side A pair #${finalistA.seed} and side B pair #${finalistB.seed}). This happens when both ` +
      `sides have a deep, mutually-dependent tie. Use the manual finalist override to pick both sides' ` +
      `finalists directly.`
  );
}

/** Validates that a joker seed hasn't already been used in a previous window for the same community. */
export function validateJokerSeedUnused(
  existingJokers: CommunityCupJokerRow[],
  side: "A" | "B",
  newRound: number,
  newSeed: number
): { valid: boolean; conflictRound?: number } {
  for (const j of existingJokers) {
    if (
      j.community_side === side &&
      j.pair_seed === newSeed &&
      j.round_no !== newRound
    ) {
      return { valid: false, conflictRound: j.round_no };
    }
  }
  return { valid: true };
}

/**
 * Every Joker round-robin round now gives each community 2 tokens, spendable
 * across divisions (never more than one per division per round, which the
 * `community_cup_jokers` PK already guarantees structurally). This is the
 * per-round, per-side cap on top of that.
 */
export const JOKER_ROUND_BUDGET = 2;

/**
 * Looks up the fixture opponent seed for one of this side's joker picks
 * (own `pair_seed` in `roundNo`), used by `validateJokerOpponentUnused` to
 * find what a past (or candidate) pick's opponent was.
 */
function findOpponentSeed(
  fixtures: CommunityCupFixture[],
  side: "A" | "B",
  roundNo: number,
  ownSeed: number
): number | undefined {
  const fixture = fixtures.find(
    (f) => f.round === roundNo && (side === "A" ? f.seedA === ownSeed : f.seedB === ownSeed)
  );
  if (!fixture) return undefined;
  return side === "A" ? fixture.seedB : fixture.seedA;
}

/**
 * Validates that a joker's opponent (the fixture pair on the other side)
 * hasn't already been targeted by a previous joker from the same community —
 * "no joker can be played against any pair more than once". Scoped to one
 * division's `existingJokers`/`fixtures`, since a "pair" is identified by
 * (division, seed): the same physical players covering another division
 * are a different pair for this rule.
 */
export function validateJokerOpponentUnused(
  existingJokers: CommunityCupJokerRow[],
  fixtures: CommunityCupFixture[],
  side: "A" | "B",
  newRound: number,
  newSeed: number
): { valid: boolean; conflictRound?: number; opponentSeed?: number } {
  const candidateOpponentSeed = findOpponentSeed(fixtures, side, newRound, newSeed);
  if (candidateOpponentSeed === undefined) return { valid: true };

  for (const j of existingJokers) {
    if (j.community_side !== side || j.pair_seed == null || j.round_no === newRound) continue;
    const pastOpponentSeed = findOpponentSeed(fixtures, side, j.round_no, j.pair_seed);
    if (pastOpponentSeed === candidateOpponentSeed) {
      return { valid: false, conflictRound: j.round_no, opponentSeed: candidateOpponentSeed };
    }
  }
  return { valid: true };
}

/**
 * Counts how many of a side's `JOKER_ROUND_BUDGET` tokens for `roundNo` are
 * already spent in OTHER divisions — a pick in `currentDivisionId` itself
 * doesn't count against the budget (editing/replacing your own division's
 * existing pick for the round is free).
 */
export function getJokerRoundBudget(
  jokersAcrossDivisions: (CommunityCupJokerRow & { division_id: string })[],
  side: "A" | "B",
  roundNo: number,
  currentDivisionId: string
): { usedElsewhere: number; exhausted: boolean } {
  const usedElsewhere = jokersAcrossDivisions.filter(
    (j) => j.community_side === side && j.round_no === roundNo && j.division_id !== currentDivisionId
  ).length;
  return { usedElsewhere, exhausted: usedElsewhere >= JOKER_ROUND_BUDGET };
}
